import { useCallback, Dispatch } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { setUserId } from '@amplitude/analytics-browser';
import { useAuthContext, AuthState } from '../providers/AuthProvider';
import {
  checkForActiveAuth,
  checkForActiveAuthComplete,
} from '../actions/candidateLogin';
import {
  nonCandidateLoggedIn,
  resetPortal,
  resetFailedPortal,
} from '../actions/reports';
import {
  isWorkosDobPending,
  getWorkosDobAuthMethod,
  WORKOS_RESET,
  workosCodeSending,
  workosCodeSent,
  workosVerifyRequest,
  workosVerifySuccess,
  workosVerifyFailure,
  workosDobRequired,
} from '../actions/workosLogin';
import {
  resetWorkosDobAnalyticsState,
  markWorkosDobMismatch,
  consumeWorkosDobAnalyticsState,
  incrementWorkosDobAttempt,
} from '../actions/workosDobAnalytics';
import { trackAnalyticsEvent } from '../actions/analytics';
import { ANALYTICS_EVENTS, ANALYTICS_PROPERTIES } from '../lib/analytics';
import { isSessionStorageSupported } from '../lib/helpers';
import { sha256 } from '../lib/sha256';
import {
  AUTH_METHODS,
  PERSONAL_CHECKS_API,
  SESSION_STORAGE_AUTH_METHOD_KEY,
  SESSION_STORAGE_CANDIDATE_IDS_KEY,
  SESSION_STORAGE_JWT_KEY,
  SESSION_STORAGE_TOKEN_KEY,
  SESSION_STORAGE_WORKOS_DOB_PENDING_KEY,
} from '../constants';

type ReduxDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

const WORKOS_AUTH_BASE = '/candidate_portal/workos_auth';

// ECDEX-3441 (MR A2): Checkr Profile session-check actions, ported from
// actions/candidateLogin.js. `dispatch` (the AuthProvider context dispatch)
// takes over the `candidateLogin`/`portalAuth` state this action used to
// update in Redux. `reduxDispatch` stays in the picture for two things that
// remain genuinely Redux's job: analytics events, and nonCandidateLoggedIn's
// portal.js case, which also resets report-list fields (reports/error/
// applicantId/firstName) that are staying in Redux per this ticket's scope
// -- so nonCandidateLoggedIn is dispatched to both today.
const CANDIDATE_PORTAL_AUTH_PATH = '/candidate_portal_auth';

const fetchCheckrProfileAuthSession = (): Promise<{
  token: string;
  candidate_ids: string[];
}> => {
  const url = new URL(CANDIDATE_PORTAL_AUTH_PATH, PERSONAL_CHECKS_API);
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  }).then(async res => {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err: any = new Error(body?.error || res.statusText);
      err.status = res.status;
      throw err;
    }
    if (!body?.token) throw new Error(body?.error || 'No token');
    return {
      token: body.token,
      candidate_ids: body.candidate_ids || [],
    };
  });
};

export const clearCheckrProfileAuthSession = (): Promise<void> => {
  const url = new URL(
    CANDIDATE_PORTAL_AUTH_PATH,
    PERSONAL_CHECKS_API,
  ).toString();
  return axios.delete(url, { withCredentials: true }).then(
    () => {},
    () => {},
  );
};

// ECDEX-3441 (MRs A3-A4): WorkOS login actions (magic-code email, DOB
// challenge, Google OAuth, logout), ported from actions/workosLogin.ts.
// `dispatchFailure` and `processAuthResponse` are the shared helpers every
// WorkOS action builds on -- kept private to this module, same as in
// workosLogin.ts, since nothing outside useAuth.ts calls them directly.
function clearWorkosAuthStorage() {
  if (!isSessionStorageSupported()) return;

  globalThis.sessionStorage.removeItem(SESSION_STORAGE_JWT_KEY);
  globalThis.sessionStorage.removeItem(SESSION_STORAGE_CANDIDATE_IDS_KEY);
  globalThis.sessionStorage.removeItem(SESSION_STORAGE_AUTH_METHOD_KEY);
  globalThis.sessionStorage.removeItem(SESSION_STORAGE_TOKEN_KEY);
  globalThis.sessionStorage.removeItem(SESSION_STORAGE_WORKOS_DOB_PENDING_KEY);
}

interface DispatchFailureOptions {
  dispatch: Dispatch<any>;
  reduxDispatch: ReduxDispatch;
  errorKey: string;
  errorType: string;
  body?: any;
  dobAttemptNumber?: number;
  failureEvent?: string;
  authMethod?: string;
}

function dispatchFailure({
  dispatch,
  reduxDispatch,
  errorKey,
  errorType,
  body,
  dobAttemptNumber,
  failureEvent = ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_LOGIN_FAILED,
  authMethod = AUTH_METHODS.WORKOS_EMAIL,
}: DispatchFailureOptions) {
  dispatch(workosVerifyFailure(errorKey));
  const errorCode = body?.errors?.[0]?.code;
  const isDobVerifyFailure = errorType === 'dobVerifyFailed';

  reduxDispatch(
    trackAnalyticsEvent(failureEvent, {
      [ANALYTICS_PROPERTIES.AUTH_METHOD]: authMethod,
      errorType,
      ...(errorCode && { errorCode }),
      ...(dobAttemptNumber != null &&
        dobAttemptNumber > 0 && {
          dob_attempt_number: dobAttemptNumber,
        }),
      ...(isDobVerifyFailure && {
        dob_error_key: errorKey,
      }),
    }),
  );

  if (isDobVerifyFailure) {
    if (errorKey === 'dobMismatch') {
      markWorkosDobMismatch();
    }
    reduxDispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_DOB_VERIFY_FAILED,
        {
          [ANALYTICS_PROPERTIES.AUTH_METHOD]: authMethod,
          dob_error_key: errorKey,
          ...(dobAttemptNumber != null &&
            dobAttemptNumber > 0 && {
              dob_attempt_number: dobAttemptNumber,
            }),
          ...(errorCode && { errorCode }),
        },
      ),
    );
  }
}

function resolveVerifyErrorKey(status: number, body: any): string {
  const errorCode = body?.errors?.[0]?.code;
  if (errorCode === 'invalid_code') return 'invalidCode';
  if (errorCode === 'no_account') return 'noAccount';
  if (status === 401) return 'invalidCode';
  return 'genericError';
}

function resolveDobVerifyErrorKey(status: number, body: any): string {
  const errorCode = body?.errors?.[0]?.code;
  if (errorCode === 'dob_mismatch') return 'dobMismatch';
  if (status === 401) return 'dobSessionExpired';
  if (status === 429) return 'dobRateLimited';
  if (status === 403 && body?.profile_deleted === true)
    return 'dobProfileDeleted';
  return 'genericError';
}

interface ProcessAuthOptions {
  failureEvent?: string;
  authMethod?: string;
  // Set by verifyWorkOSDob -- tags the login success event with dob_challenge
  // so analytics can distinguish DOB-gated logins from direct logins.
  fromDobChallenge?: boolean;
  dobAttemptNumber?: number;
}

// The three successful-response outcomes of processAuthResponse, split out so
// each reads on its own (and so processAuthResponse stays under Sonar's
// cognitive-complexity limit).

function handleDobRequired(dispatch: Dispatch<any>, authMethod: string) {
  clearWorkosAuthStorage();
  if (isSessionStorageSupported()) {
    globalThis.sessionStorage.setItem(
      SESSION_STORAGE_AUTH_METHOD_KEY,
      authMethod,
    );
    globalThis.sessionStorage.setItem(
      SESSION_STORAGE_WORKOS_DOB_PENDING_KEY,
      'true',
    );
  }
  dispatch(workosDobRequired());
}

function handleNonCandidateLogin(
  dispatch: Dispatch<any>,
  reduxDispatch: ReduxDispatch,
  authMethod: string,
  fromDobChallenge: boolean,
) {
  if (isSessionStorageSupported()) {
    globalThis.sessionStorage.removeItem(SESSION_STORAGE_JWT_KEY);
    globalThis.sessionStorage.removeItem(
      SESSION_STORAGE_WORKOS_DOB_PENDING_KEY,
    );
    globalThis.sessionStorage.setItem(
      SESSION_STORAGE_AUTH_METHOD_KEY,
      authMethod,
    );
    globalThis.sessionStorage.setItem(SESSION_STORAGE_CANDIDATE_IDS_KEY, '');
  }
  reduxDispatch(resetPortal());
  dispatch(
    workosVerifySuccess({
      candidateJwt: '',
      candidateIds: [],
      authMethod,
    }),
  );
  dispatch(nonCandidateLoggedIn());
  reduxDispatch(nonCandidateLoggedIn());
  reduxDispatch(
    trackAnalyticsEvent(ANALYTICS_EVENTS.CANDIDATE_PORTAL_LOGIN_SUCCESS, {
      [ANALYTICS_PROPERTIES.AUTH_METHOD]: authMethod,
      [ANALYTICS_PROPERTIES.IS_CANDIDATE]: false,
      ...(fromDobChallenge ? consumeWorkosDobAnalyticsState() : undefined),
    }),
  );
}

function handleCandidateLogin(
  dispatch: Dispatch<any>,
  reduxDispatch: ReduxDispatch,
  token: string,
  candidateIds: string[] | undefined,
  authMethod: string,
  fromDobChallenge: boolean,
) {
  if (isSessionStorageSupported()) {
    globalThis.sessionStorage.removeItem(
      SESSION_STORAGE_WORKOS_DOB_PENDING_KEY,
    );
    globalThis.sessionStorage.setItem(SESSION_STORAGE_JWT_KEY, token);
    globalThis.sessionStorage.setItem(
      SESSION_STORAGE_CANDIDATE_IDS_KEY,
      Array.isArray(candidateIds) ? candidateIds.join(',') : '',
    );
    globalThis.sessionStorage.setItem(
      SESSION_STORAGE_AUTH_METHOD_KEY,
      authMethod,
    );
  }

  reduxDispatch(resetPortal());

  dispatch(
    workosVerifySuccess({
      candidateJwt: token,
      candidateIds: candidateIds || [],
      authMethod,
    }),
  );
  reduxDispatch(
    trackAnalyticsEvent(ANALYTICS_EVENTS.CANDIDATE_PORTAL_LOGIN_SUCCESS, {
      [ANALYTICS_PROPERTIES.AUTH_METHOD]: authMethod,
      [ANALYTICS_PROPERTIES.IS_CANDIDATE]:
        Array.isArray(candidateIds) && candidateIds.length > 0,
      ...(fromDobChallenge ? consumeWorkosDobAnalyticsState() : undefined),
    }),
  );
}

function processAuthResponse(
  dispatch: Dispatch<any>,
  reduxDispatch: ReduxDispatch,
  res: Response,
  body: any,
  errorType: string,
  getErrorKey: (status: number, body: any) => string = () => 'genericError',
  options: ProcessAuthOptions = {},
): boolean {
  const {
    failureEvent = ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_LOGIN_FAILED,
    authMethod = AUTH_METHODS.WORKOS_EMAIL,
    fromDobChallenge = false,
    dobAttemptNumber,
  } = options;

  if (!res.ok) {
    dispatchFailure({
      dispatch,
      reduxDispatch,
      errorKey: getErrorKey(res.status, body),
      body,
      dobAttemptNumber,
      errorType,
      failureEvent,
      authMethod,
    });
    return false;
  }

  const {
    token,
    candidate_ids: candidateIds,
    dob_required: dobRequired,
  } = body;

  if (dobRequired) {
    handleDobRequired(dispatch, authMethod);
    return true;
  }

  // Non-candidate: authenticated but no candidate records. Treated the same
  // whether or not a token came back (it's useless without IDs).
  if (Array.isArray(candidateIds) && candidateIds.length === 0) {
    handleNonCandidateLogin(
      dispatch,
      reduxDispatch,
      authMethod,
      fromDobChallenge,
    );
    return true;
  }

  if (!token) {
    dispatchFailure({
      dispatch,
      reduxDispatch,
      errorKey: getErrorKey(res.status, body),
      body,
      dobAttemptNumber,
      errorType: body.parseError ? 'jsonParseError' : 'missingToken',
      failureEvent,
      authMethod,
    });
    return false;
  }

  handleCandidateLogin(
    dispatch,
    reduxDispatch,
    token,
    candidateIds,
    authMethod,
    fromDobChallenge,
  );
  return true;
}

export interface UseAuthReturn {
  state: AuthState;
  checkForActiveAuthSession: () => Promise<void>;
  clearCheckrProfileAuthSession: () => Promise<void>;
  sendWorkOSMagicCode: (email: string) => Promise<void>;
  verifyWorkOSMagicCode: (email: string, code: string) => Promise<void>;
  verifyWorkOSDob: (dob: string) => Promise<void>;
  initiateWorkOSGoogleAuth: () => void;
  exchangeWorkOSOAuthCode: (code: string) => Promise<void>;
  workosLogout: () => Promise<void>;
  workosReset: () => void;
  workosResetUi: () => void;
}

const useAuth = (): UseAuthReturn => {
  const { state, dispatch } = useAuthContext();
  const reduxDispatch = useDispatch();

  const checkForActiveAuthSession = useCallback(async () => {
    // Mirrors completeActiveAuthCheck in actions/candidateLogin.js: restoring a
    // session also clears a stale failed report fetch in the portal slice.
    const completeAuthCheck = (payload: {
      loggedIn: boolean;
      candidateJwt: string;
      candidateIds: string[];
      authMethod?: string | null;
    }) => {
      dispatch(checkForActiveAuthComplete(payload));
      if (payload.loggedIn) {
        reduxDispatch(resetFailedPortal());
      }
    };

    dispatch(checkForActiveAuth());

    if (!isSessionStorageSupported()) {
      completeAuthCheck({
        loggedIn: false,
        candidateJwt: '',
        candidateIds: [],
      });
      return;
    }

    const existingSession = globalThis.sessionStorage.getItem(
      SESSION_STORAGE_JWT_KEY,
    );
    const existingCandidateIds = globalThis.sessionStorage.getItem(
      SESSION_STORAGE_CANDIDATE_IDS_KEY,
    );

    if (existingSession) {
      const candidateJwt = existingSession;
      const candidateIds = existingCandidateIds
        ? existingCandidateIds.split(',')
        : [];
      const authMethod =
        globalThis.sessionStorage.getItem(SESSION_STORAGE_AUTH_METHOD_KEY) ||
        null;
      completeAuthCheck({
        loggedIn: true,
        candidateJwt,
        candidateIds,
        authMethod,
      });
      return;
    }

    const previousAuthMethod = globalThis.sessionStorage.getItem(
      SESSION_STORAGE_AUTH_METHOD_KEY,
    );
    // Reached only when existingSession is falsy (the block above returns early
    // otherwise), so both branches here run without a live JWT.
    if (previousAuthMethod && isWorkosDobPending()) {
      completeAuthCheck({
        loggedIn: false,
        candidateJwt: '',
        candidateIds: [],
        authMethod: previousAuthMethod,
      });
      return;
    }
    if (previousAuthMethod) {
      globalThis.sessionStorage.removeItem(SESSION_STORAGE_JWT_KEY);
      globalThis.sessionStorage.removeItem(SESSION_STORAGE_CANDIDATE_IDS_KEY);
      globalThis.sessionStorage.removeItem(SESSION_STORAGE_AUTH_METHOD_KEY);
      globalThis.sessionStorage.removeItem(SESSION_STORAGE_TOKEN_KEY);
      reduxDispatch(
        trackAnalyticsEvent(ANALYTICS_EVENTS.CANDIDATE_PORTAL_SESSION_EXPIRED, {
          [ANALYTICS_PROPERTIES.AUTH_METHOD]: previousAuthMethod,
        }),
      );
      completeAuthCheck({
        loggedIn: false,
        candidateJwt: '',
        candidateIds: [],
      });
      return;
    }

    try {
      const { token, candidate_ids: candidateIdsResult } =
        await fetchCheckrProfileAuthSession();

      // Non-candidate: API returned a token but no candidate records.
      // Clear all sessionStorage auth keys so the next page load doesn't
      // trigger "session expired" detection (previousAuthMethod && !existingSession).
      // The cookie persists independently, so fetchCheckrProfileAuthSession will
      // re-authenticate on the next full page load.
      if (!candidateIdsResult || candidateIdsResult.length === 0) {
        if (isSessionStorageSupported()) {
          globalThis.sessionStorage.removeItem(SESSION_STORAGE_JWT_KEY);
          globalThis.sessionStorage.removeItem(
            SESSION_STORAGE_CANDIDATE_IDS_KEY,
          );
          globalThis.sessionStorage.removeItem(SESSION_STORAGE_AUTH_METHOD_KEY);
          globalThis.sessionStorage.removeItem(SESSION_STORAGE_TOKEN_KEY);
        }

        reduxDispatch(
          trackAnalyticsEvent(
            ANALYTICS_EVENTS.CANDIDATE_PORTAL_CHECKR_PROFILE_AUTH_LOGIN_SUCCESS,
            { [ANALYTICS_PROPERTIES.AUTH_METHOD]: AUTH_METHODS.CHECKR_PROFILE },
          ),
        );

        completeAuthCheck({
          loggedIn: true,
          candidateJwt: '',
          candidateIds: [],
          authMethod: AUTH_METHODS.CHECKR_PROFILE,
        });
        dispatch(nonCandidateLoggedIn());
        reduxDispatch(nonCandidateLoggedIn());
        return;
      }

      if (isSessionStorageSupported()) {
        globalThis.sessionStorage.setItem(SESSION_STORAGE_JWT_KEY, token);
        globalThis.sessionStorage.setItem(
          SESSION_STORAGE_CANDIDATE_IDS_KEY,
          Array.isArray(candidateIdsResult) ? candidateIdsResult.join(',') : '',
        );
        globalThis.sessionStorage.setItem(
          SESSION_STORAGE_AUTH_METHOD_KEY,
          AUTH_METHODS.CHECKR_PROFILE,
        );
      }

      reduxDispatch(
        trackAnalyticsEvent(
          ANALYTICS_EVENTS.CANDIDATE_PORTAL_CHECKR_PROFILE_AUTH_LOGIN_SUCCESS,
          { [ANALYTICS_PROPERTIES.AUTH_METHOD]: AUTH_METHODS.CHECKR_PROFILE },
        ),
      );

      completeAuthCheck({
        loggedIn: true,
        candidateJwt: token,
        candidateIds: candidateIdsResult || [],
        authMethod: AUTH_METHODS.CHECKR_PROFILE,
      });
    } catch {
      completeAuthCheck({
        loggedIn: false,
        candidateJwt: '',
        candidateIds: [],
      });
    }
  }, [dispatch, reduxDispatch]);

  const sendWorkOSMagicCode = useCallback(
    async (email: string) => {
      dispatch(workosCodeSending());
      reduxDispatch(
        trackAnalyticsEvent(
          ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_LOGIN_SUBMITTED,
          { [ANALYTICS_PROPERTIES.AUTH_METHOD]: AUTH_METHODS.WORKOS_EMAIL },
        ),
      );

      try {
        const url = new URL(
          `${WORKOS_AUTH_BASE}/magic_auth`,
          PERSONAL_CHECKS_API,
        );
        await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email }),
        });

        // Always transition to code step regardless of response status
        // to prevent email enumeration
        dispatch(workosCodeSent(email));
      } catch {
        // Even on network error, transition to code step
        dispatch(workosCodeSent(email));
      }
    },
    [dispatch, reduxDispatch],
  );

  const verifyWorkOSMagicCode = useCallback(
    async (email: string, code: string) => {
      dispatch(workosVerifyRequest());

      try {
        const payload = { email, code };

        const url = new URL(`${WORKOS_AUTH_BASE}/verify`, PERSONAL_CHECKS_API);
        const res = await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        });

        let body: any;
        try {
          body = await res.json();
        } catch {
          body = { parseError: true };
        }
        try {
          setUserId(await sha256(email));
        } catch {
          // Analytics must never break the login flow
        }
        processAuthResponse(
          dispatch,
          reduxDispatch,
          res,
          body,
          'verifyFailed',
          resolveVerifyErrorKey,
        );
      } catch {
        dispatchFailure({
          dispatch,
          reduxDispatch,
          errorKey: 'genericError',
          errorType: 'networkError',
        });
      }
    },
    [dispatch, reduxDispatch],
  );

  const initiateWorkOSGoogleAuth = useCallback(() => {
    if (isSessionStorageSupported()) {
      globalThis.sessionStorage.removeItem(
        SESSION_STORAGE_WORKOS_DOB_PENDING_KEY,
      );
    }
    reduxDispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_GOOGLE_OAUTH_INITIATED,
        { [ANALYTICS_PROPERTIES.AUTH_METHOD]: AUTH_METHODS.WORKOS_GOOGLE },
      ),
    );

    const redirectUri = `${globalThis.location.origin}/login/email/callback`;
    const url = new URL(
      `${WORKOS_AUTH_BASE}/oauth/authorize`,
      PERSONAL_CHECKS_API,
    );
    url.searchParams.set('redirect_uri', redirectUri);
    globalThis.location.href = url.toString();
  }, [reduxDispatch]);

  const exchangeWorkOSOAuthCode = useCallback(
    async (code: string) => {
      dispatch(workosVerifyRequest());

      try {
        const url = new URL(
          `${WORKOS_AUTH_BASE}/oauth/exchange`,
          PERSONAL_CHECKS_API,
        );
        const res = await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code }),
        });

        let body: any;
        try {
          body = await res.json();
        } catch {
          body = { parseError: true };
        }
        processAuthResponse(
          dispatch,
          reduxDispatch,
          res,
          body,
          'exchange_failed',
          () => 'oauthError',
          {
            failureEvent:
              ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_GOOGLE_OAUTH_FAILED,
            authMethod: AUTH_METHODS.WORKOS_GOOGLE,
          },
        );
      } catch {
        dispatchFailure({
          dispatch,
          reduxDispatch,
          errorKey: 'oauthError',
          errorType: 'network_error',
          failureEvent:
            ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_GOOGLE_OAUTH_FAILED,
          authMethod: AUTH_METHODS.WORKOS_GOOGLE,
        });
      }
    },
    [dispatch, reduxDispatch],
  );

  const verifyWorkOSDob = useCallback(
    async (dob: string) => {
      // workosVerifyRequest sets processing:true; processAuthResponse and
      // dispatchFailure both dispatch actions that reset it to false.
      const attemptNumber = incrementWorkosDobAttempt();
      const authMethod = getWorkosDobAuthMethod();
      dispatch(workosVerifyRequest());
      reduxDispatch(
        trackAnalyticsEvent(
          ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_DOB_SUBMITTED,
          {
            [ANALYTICS_PROPERTIES.AUTH_METHOD]: authMethod,
            ...(attemptNumber > 0 && { dob_attempt_number: attemptNumber }),
            dob_is_retry: attemptNumber > 1,
          },
        ),
      );

      try {
        const url = new URL(
          `${WORKOS_AUTH_BASE}/verify_dob`,
          PERSONAL_CHECKS_API,
        );
        const res = await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ dob }),
        });

        let body: any;
        try {
          body = await res.json();
        } catch {
          body = { parseError: true };
        }
        if (res.status === 401 && isSessionStorageSupported()) {
          globalThis.sessionStorage.removeItem(
            SESSION_STORAGE_WORKOS_DOB_PENDING_KEY,
          );
        }
        processAuthResponse(
          dispatch,
          reduxDispatch,
          res,
          body,
          'dobVerifyFailed',
          resolveDobVerifyErrorKey,
          {
            fromDobChallenge: true,
            authMethod,
            dobAttemptNumber: attemptNumber,
          },
        );
      } catch {
        dispatchFailure({
          dispatch,
          reduxDispatch,
          errorKey: 'genericError',
          errorType: 'networkError',
          dobAttemptNumber: attemptNumber,
        });
      }
    },
    [dispatch, reduxDispatch],
  );

  // Best-effort session revocation; never throws.
  const workosLogout = useCallback(async () => {
    try {
      const url = new URL(`${WORKOS_AUTH_BASE}/session`, PERSONAL_CHECKS_API);
      await fetch(url.toString(), {
        method: 'DELETE',
        credentials: 'include',
      });
    } catch {
      // Best-effort session revocation
    }
  }, []);

  const workosReset = useCallback(() => {
    resetWorkosDobAnalyticsState();
    clearWorkosAuthStorage();
    dispatch({ type: WORKOS_RESET });
  }, [dispatch]);

  const workosResetUi = useCallback(() => {
    resetWorkosDobAnalyticsState();
    dispatch({ type: WORKOS_RESET });
  }, [dispatch]);

  return {
    state,
    checkForActiveAuthSession,
    clearCheckrProfileAuthSession,
    sendWorkOSMagicCode,
    verifyWorkOSMagicCode,
    verifyWorkOSDob,
    initiateWorkOSGoogleAuth,
    exchangeWorkOSOAuthCode,
    workosLogout,
    workosReset,
    workosResetUi,
  };
};

export default useAuth;
